import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {Funktionsbereich, FunktionsbereichDetail, Rolle, RolleDetail} from '../../../clients/benutzerverwaltung';
import {Observable} from 'rxjs';
import {
    GetFunktionsbereichAdminsAction,
    GetFunktionsbereichDetailAction,
    GetFunktionsbereicheAction,
    GetFunktionsbereichFreigabeRollenAction,
    GetFunktionsbereichRollenAction,
    GetRolleDetailAction
} from '../../../client-ngrx/benutzerverwaltung';
import {filter, flatMap, map, withLatestFrom} from 'rxjs/operators';
import {BenutzerverwaltungEntityTypes, EinlasskontrolleEntityTypes, IncidentsEntityTypes, KommunikationEntityTypes} from '../../../client-constants';
import {getFunktionsbereichDetail, getFunktionsbereichFreigabeRolleListe, getMitteilungsVorlageDetail, getRolleDetail, PageState} from './funktionsbereiche-rollen.reducer';
import {WS_READ_MODEL_UPDATED, WsReadModelUpdatedAction} from '../../websocket.actions';
import {ReadModelUpdatedNotification} from '../../../clients/api';
import {NotificationService} from '@orga-app/providers/notification.service';
import {
    AktuellenFunktionsbereichSetzenAction,
    AktuellerFunktionsbereichWurdeGeloescht,
    AktuelleRolleSetzenAction,
    AktuelleRolleWurdeGeloescht
} from './funktionsbereiche-rollen.actions.ui';
import {GetMitteilungsVorlageDetailAction, GetRolleMitteilungOptionenAction, GetRolleMitteilungsVorlageListeAction} from '../../../client-ngrx/kommunikation';
import FUNKTIONSBEREICH_ENTITY = BenutzerverwaltungEntityTypes.FUNKTIONSBEREICH_ENTITY;
import FUNKTIONSBEREICH_ENTITY_PART_FREIGABE_ROLLEN = BenutzerverwaltungEntityTypes.FUNKTIONSBEREICH_ENTITY_PART_FREIGABE_ROLLEN;
import FUNKTIONSBEREICH_ENTITY_PART_ADMIN_BENUTZER = BenutzerverwaltungEntityTypes.FUNKTIONSBEREICH_ENTITY_PART_ADMIN_BENUTZER;
import FUNKTIONSBEREICH_ENTITY_PART_DETAIL = BenutzerverwaltungEntityTypes.FUNKTIONSBEREICH_ENTITY_PART_DETAIL;
import ROLLE_ENTITY = BenutzerverwaltungEntityTypes.ROLLE_ENTITY;
import ROLLE_ENTITY_PART_DETAIL = BenutzerverwaltungEntityTypes.ROLLE_ENTITY_PART_DETAIL;
import {GetFunktionsbereichAbteilungAction, GetRolleAbteilungAction, GetRolleZentraleAction} from '../../../client-ngrx/incidents';
import {getIstEinlasskontrolleLizenzAktiv, getIstIncidentsLizenzAktiv} from '@orga-app/mandant/reducer';
import {GetEinlasskontrolleRolleAction} from '../../../client-ngrx/einlasskontrolle';
import MITTEILUNGS_VORLAGE_ENTITY = KommunikationEntityTypes.MITTEILUNGS_VORLAGE_ENTITY;
import {MitteilungsVorlage} from '../../../clients/kommunikation';
import {ListInputItem} from '../../shared/sopi-list/sopi-list.model';

@Injectable()
export class EffectsWs {
    @Effect()
    readModelUpdaterFunktionsbereiche$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === FUNKTIONSBEREICH_ENTITY;
        })
        , map((x: WsReadModelUpdatedAction) => {
            return <Funktionsbereich> JSON.parse(x.message.entity);
        })
        , map((x: Funktionsbereich) => {
            return new GetFunktionsbereicheAction();
        })
    );

    @Effect()
    readModelUpdaterFunktionsbereichRollenMitteilungenOptionen$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === ROLLE_ENTITY;
        })
        , withLatestFrom(this.store.pipe(select(getRolleDetail)))
        , filter(([x, rolleDetail]: [WsReadModelUpdatedAction, RolleDetail]) =>
            rolleDetail !== null &&
            rolleDetail.rolle_id !== undefined
        )
        , map(([x, rolleDetail]: [WsReadModelUpdatedAction, RolleDetail]) => {
            return new GetRolleMitteilungOptionenAction(rolleDetail.rolle_id);
        })
    );

    @Effect()
    readModelUpdaterFunktionsbereich$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === FUNKTIONSBEREICH_ENTITY && x.message.entity_part.toUpperCase() === FUNKTIONSBEREICH_ENTITY_PART_DETAIL;
        })
        , map((x: WsReadModelUpdatedAction) => {
            return <ReadModelUpdatedNotification> x.message;
        })
        , withLatestFrom(this.store.pipe(select(getFunktionsbereichDetail)))
        , filter(([x, funktionsbereich]) => funktionsbereich !== null && funktionsbereich !== undefined)
        , flatMap(([x, funktionsbereich]: [ReadModelUpdatedNotification, FunktionsbereichDetail]) => {
            const res = [];
            if (x.method === 'DELETE') {
                this.notificationService.showMessage('Der ausgewählte Funktionsbereich wurde von einem anderen Benutzer entfernt.');
                return [
                    new AktuellenFunktionsbereichSetzenAction(null),
                    new AktuellerFunktionsbereichWurdeGeloescht()
                ];
            } else {
                if ((<FunktionsbereichDetail> JSON.parse(x.entity)).funktionsbereich_id === funktionsbereich.funktionsbereich_id) {
                    res.push(new GetFunktionsbereichDetailAction(funktionsbereich.funktionsbereich_id));
                }
            }

            res.push(new GetFunktionsbereichFreigabeRollenAction(funktionsbereich.funktionsbereich_id));
            return res;
        })
    );

    @Effect()
    readModelUpdaterFunktionsbereichAdmins$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === FUNKTIONSBEREICH_ENTITY && x.message.entity_part.toUpperCase() === FUNKTIONSBEREICH_ENTITY_PART_ADMIN_BENUTZER;
        })
        , withLatestFrom(this.store.pipe(select(getFunktionsbereichDetail)))
        , filter(([x, funktionsbereich]: [WsReadModelUpdatedAction, FunktionsbereichDetail]) => funktionsbereich !== null && funktionsbereich.funktionsbereich_id !== undefined)
        , map(([x, funktionsbereich]: [WsReadModelUpdatedAction, FunktionsbereichDetail]) => {
            return [funktionsbereich, <Funktionsbereich> JSON.parse(x.message.entity)];
        })
        , filter(([detail, fb]: [FunktionsbereichDetail, Funktionsbereich]) => {
            return fb.funktionsbereich_id === detail.funktionsbereich_id;
        })
        , map(([detail, fb]: [FunktionsbereichDetail, Funktionsbereich]) => {
            return new GetFunktionsbereichAdminsAction(fb.funktionsbereich_id);
        })
    );

    @Effect()
    readModelUpdaterFunktionsbereichRollen$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === ROLLE_ENTITY;
        })
        , withLatestFrom(this.store.pipe(select(getFunktionsbereichDetail)), this.store.pipe(select(getRolleDetail)))
        , filter(([x, funktionsbereich, rolle]: [WsReadModelUpdatedAction, FunktionsbereichDetail, RolleDetail]) => funktionsbereich !== null && funktionsbereich.funktionsbereich_id !== undefined)
        , flatMap(([x, funktionsbereich, rolle]: [WsReadModelUpdatedAction, FunktionsbereichDetail, RolleDetail]) => {
            const res = [];
            res.push(new GetFunktionsbereichRollenAction(funktionsbereich.funktionsbereich_id));

            if (rolle != null && rolle.rolle_id === (<Rolle> JSON.parse(x.message.entity)).rolle_id) {
                if ((<ReadModelUpdatedNotification> x.message).method === 'DELETE') {
                    this.notificationService.showMessage('Die ausgewählte Rolle wurde von einem anderen Benutzer entfernt.');
                    res.push(new AktuelleRolleSetzenAction(null));
                    res.push(new AktuelleRolleWurdeGeloescht());
                } else {
                    res.push(new AktuelleRolleSetzenAction(<Rolle> JSON.parse(x.message.entity)));
                    res.push(new GetRolleDetailAction(rolle.rolle_id));
                }
            }

            return res;
        })
    );

    @Effect()
    readModelUpdaterFunktionsbereichRollenDetail$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === ROLLE_ENTITY && x.message.entity_part.toUpperCase() === ROLLE_ENTITY_PART_DETAIL;
        })
        , withLatestFrom(this.store.pipe(select(getFunktionsbereichDetail)), this.store.pipe(select(getRolleDetail)))
        , filter(([x, funktionsbereich, rolle]: [WsReadModelUpdatedAction, FunktionsbereichDetail, RolleDetail]) =>
            funktionsbereich !== null &&
            funktionsbereich.funktionsbereich_id !== undefined &&
            rolle !== null &&
            rolle.rolle_id === (<RolleDetail> JSON.parse(x.message.entity)).rolle_id
        )
        , flatMap(([x, funktionsbereich, rolle]: [WsReadModelUpdatedAction, FunktionsbereichDetail, RolleDetail]) => {
            const res = [];
            res.push(new GetFunktionsbereichRollenAction(funktionsbereich.funktionsbereich_id));

            if (rolle != null && rolle.rolle_id === (<RolleDetail> JSON.parse(x.message.entity)).rolle_id) {
                if ((<ReadModelUpdatedNotification> x.message).method === 'DELETE') {
                    this.notificationService.showMessage('Die ausgewählte Rolle wurde von einem anderen Benutzer entfernt.');
                    res.push(new AktuelleRolleSetzenAction(null));
                    res.push(new AktuelleRolleWurdeGeloescht());
                } else {
                    res.push(new AktuelleRolleSetzenAction(<Rolle> JSON.parse(x.message.entity)));
                    res.push(new GetRolleDetailAction(rolle.rolle_id));
                }
            }

            return res;
        })
    );

    @Effect()
    readModelUpdaterFunktionsbereichFreigabeRollen$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === FUNKTIONSBEREICH_ENTITY && x.message.entity_part.toUpperCase() === FUNKTIONSBEREICH_ENTITY_PART_FREIGABE_ROLLEN;
        })
        , withLatestFrom(this.store.pipe(select(getFunktionsbereichDetail)))
        , filter(([x, funktionsbereich]: [WsReadModelUpdatedAction, FunktionsbereichDetail]) =>
            funktionsbereich !== null &&
            funktionsbereich.funktionsbereich_id !== undefined
        )
        , map(([x, funktionsbereich]: [WsReadModelUpdatedAction, FunktionsbereichDetail]) => {
            return new GetFunktionsbereichFreigabeRollenAction(funktionsbereich.funktionsbereich_id);
        })
    );

    @Effect()
    readModelUpdaterFunktionsbereichFreigabeRollenAenderungRolle$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , filter((x: WsReadModelUpdatedAction) => {
            return x.message.entity_type.toUpperCase() === ROLLE_ENTITY && x.message.entity_part.toUpperCase() === ROLLE_ENTITY_PART_DETAIL;
        })
        , withLatestFrom(this.store.pipe(select(getFunktionsbereichDetail)),
            this.store.pipe(select(getFunktionsbereichFreigabeRolleListe)))
        , filter(([x, funktionsbereich, freigabeRollen]: [WsReadModelUpdatedAction, FunktionsbereichDetail, Array<ListInputItem>]) =>
            freigabeRollen !== null && freigabeRollen.length > 0)
        , map(([x, funktionsbereich, freigabeRollen]) => {
            return [x, funktionsbereich, freigabeRollen, <Rolle> JSON.parse(x.message.entity)];
        })
        , filter(([x, funktionsbereich, freigabeRollen, rolle]: [WsReadModelUpdatedAction, FunktionsbereichDetail, Array<ListInputItem>, Rolle]) =>
            freigabeRollen !== null && freigabeRollen.findIndex(y => y.itemId === rolle.rolle_id) > -1)
        , map(([x, funktionsbereich, freigabeRollen, rolle]) => [x, funktionsbereich])
        , filter(([x, funktionsbereich]: [WsReadModelUpdatedAction, FunktionsbereichDetail]) =>
            funktionsbereich !== null &&
            funktionsbereich.funktionsbereich_id !== undefined
        )
        , map(([x, funktionsbereich]: [WsReadModelUpdatedAction, FunktionsbereichDetail]) => {
            return new GetFunktionsbereichFreigabeRollenAction(funktionsbereich.funktionsbereich_id);
        })
    );


    @Effect()
    readModelUpdaterFunktionsbereichAbteilungsLeitstand$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , withLatestFrom(this.store.pipe(select(getFunktionsbereichDetail)), this.store.pipe(select(getIstIncidentsLizenzAktiv)))
        , filter(([x, funktionsbereich, istIncidentsLizenzAktiv]: [WsReadModelUpdatedAction, FunktionsbereichDetail, boolean]) =>
            istIncidentsLizenzAktiv &&
            funktionsbereich !== null &&
            funktionsbereich.funktionsbereich_id !== undefined &&
            x.message.entity_type.toUpperCase() === IncidentsEntityTypes.FUNKTIONSBEREICH_ENTITY
        )
        , map(([x, funktionsbereich, istIncidentsLizenzAktiv]: [WsReadModelUpdatedAction, FunktionsbereichDetail, boolean]) => {
            return new GetFunktionsbereichAbteilungAction(funktionsbereich.funktionsbereich_id);
        })
    );

    @Effect()
    readModelUpdaterRolleZentralLeitstand$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , withLatestFrom(this.store.pipe(select(getRolleDetail)), this.store.pipe(select(getIstIncidentsLizenzAktiv)))
        , filter(([x, rolleDetail, istIncidentsLizenzAktiv]: [WsReadModelUpdatedAction, RolleDetail, boolean]) =>
            istIncidentsLizenzAktiv &&
            rolleDetail !== null &&
            rolleDetail.rolle_id !== undefined &&
            x.message.entity_type.toUpperCase() === IncidentsEntityTypes.ROLLE_ENTITY && x.message.entity_part.toUpperCase() === IncidentsEntityTypes.ROLLE_ENTITY_PART_ZENTRALE
        )
        , map(([x, rolleDetail, istIncidentsLizenzAktiv]: [WsReadModelUpdatedAction, RolleDetail, boolean]) => {
            return new GetRolleZentraleAction(rolleDetail.rolle_id);
        })
    );

    @Effect()
    readModelUpdaterRolleAbteilungsLeitstand$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , withLatestFrom(this.store.pipe(select(getRolleDetail)), this.store.pipe(select(getIstIncidentsLizenzAktiv)))
        , filter(([x, rolleDetail, istIncidentsLizenzAktiv]: [WsReadModelUpdatedAction, RolleDetail, boolean]) =>
            istIncidentsLizenzAktiv &&
            rolleDetail !== null &&
            rolleDetail.rolle_id !== undefined &&
            x.message.entity_type.toUpperCase() === IncidentsEntityTypes.ROLLE_ENTITY && x.message.entity_part.toUpperCase() === IncidentsEntityTypes.ROLLE_ENTITY_PART_ABTEILUNG
        )
        , map(([x, rolleDetail, istIncidentsLizenzAktiv]: [WsReadModelUpdatedAction, RolleDetail, boolean]) => {
            return new GetRolleAbteilungAction(rolleDetail.rolle_id);
        })
    );

    @Effect()
    readModelUpdaterRolleEinlasskontrolle$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , withLatestFrom(this.store.pipe(select(getRolleDetail)), this.store.pipe(select(getIstEinlasskontrolleLizenzAktiv)))
        , filter(([x, rolleDetail, istEinlasskontrolleLizenzAktiv]: [WsReadModelUpdatedAction, RolleDetail, boolean]) =>
            istEinlasskontrolleLizenzAktiv &&
            rolleDetail !== null &&
            rolleDetail.rolle_id !== undefined &&
            x.message.entity_type.toUpperCase() === EinlasskontrolleEntityTypes.ROLLE_ENTITY
        )
        , map(([x, rolleDetail, istEinlasskontrolleLizenzAktiv]: [WsReadModelUpdatedAction, RolleDetail, boolean]) => {
            return new GetEinlasskontrolleRolleAction(rolleDetail.rolle_id);
        })
    );

    @Effect()
    readModelUpdaterRolleMitteilungsListe$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , withLatestFrom(this.store.pipe(select(getRolleDetail)))
        , filter(([x, rolleDetail]: [WsReadModelUpdatedAction, RolleDetail]) =>
            rolleDetail !== null &&
            rolleDetail.rolle_id !== undefined &&
            x.message.entity_type.toUpperCase() === MITTEILUNGS_VORLAGE_ENTITY
        )
        , map(([x, rolleDetail]: [WsReadModelUpdatedAction, RolleDetail]) => {
            return new GetRolleMitteilungsVorlageListeAction(rolleDetail.rolle_id);
        })
    );

    @Effect()
    readModelUpdaterRolleMitteilungsVorlageDetail$: Observable<Action> = this.actions$.pipe(
        ofType<WsReadModelUpdatedAction>(WS_READ_MODEL_UPDATED)
        , withLatestFrom(this.store.pipe(select(getRolleDetail)),
            this.store.pipe(select(getMitteilungsVorlageDetail)))
        , filter(([x, rolleDetail, vorlageDetail]) =>
            rolleDetail !== null &&
            rolleDetail.rolle_id !== undefined &&
            vorlageDetail !== null &&
            x.message.entity_type.toUpperCase() === MITTEILUNGS_VORLAGE_ENTITY &&
            vorlageDetail.mitteilungs_vorlage_id === (<MitteilungsVorlage> JSON.parse(x.message.entity)).mitteilungs_vorlage_id
        )
        , map(([x, rolleDetail, vorlageDetail]) => {
            return new GetMitteilungsVorlageDetailAction(vorlageDetail.mitteilungs_vorlage_id);
        })
    );

    constructor(private actions$: Actions,
                private store: Store<PageState>,
                private notificationService: NotificationService) {
    }
}
